/**
 * Country -> region mapping shared between the storefront component
 * (frontend/js/novicell_store-locator.js) and the Node import script
 * (scripts/import-stores.mjs).
 *
 * Update REGIONS to add a new region tab; the country list under each
 * entry is matched case-insensitively, so the CSV's "Spain"/"SPAIN"/
 * "spain" all resolve to `southern_europe`. Unknown countries fall
 * back to `rest_of_world`.
 *
 * `codes` holds the ISO 3166-1 alpha-2 codes for the same countries.
 * They drive IP-based region detection on the storefront, where the
 * geolocation API returns codes ("DK") rather than names ("Denmark").
 */

export const REGIONS = [
  {
    key: 'northern_europe',
    countries: ['Denmark', 'Sweden', 'Norway', 'Finland', 'Iceland', 'Faroe Islands'],
    codes: ['DK', 'SE', 'NO', 'FI', 'IS', 'FO'],
  },
  {
    key: 'central_europe',
    countries: [
      'Germany',
      'Netherlands',
      'Belgium',
      'Austria',
      'Switzerland',
      'Luxembourg',
      'Hungary',
      'Czech Republic',
      'Czechia',
      'Poland',
      'Slovakia',
      'Slovenia',
      'Liechtenstein',
    ],
    codes: ['DE', 'NL', 'BE', 'AT', 'CH', 'LU', 'HU', 'CZ', 'PL', 'SK', 'SI', 'LI'],
  },
  {
    key: 'southern_europe',
    countries: ['Spain', 'Italy', 'Portugal', 'Greece', 'Malta', 'Cyprus', 'Croatia'],
    codes: ['ES', 'IT', 'PT', 'GR', 'MT', 'CY', 'HR'],
  },
  {
    key: 'eastern_europe',
    countries: ['Estonia', 'Latvia', 'Lithuania', 'Romania', 'Bulgaria', 'Ukraine', 'Serbia'],
    codes: ['EE', 'LV', 'LT', 'RO', 'BG', 'UA', 'RS'],
  },
  {
    key: 'united_kingdom',
    countries: ['United Kingdom', 'UK', 'Great Britain', 'England', 'Scotland', 'Wales', 'Ireland'],
    codes: ['GB', 'IE'],
  },
  {
    key: 'north_america',
    countries: ['United States', 'USA', 'US', 'Canada'],
    codes: ['US', 'CA'],
  },
  {
    key: 'central_america',
    countries: ['Mexico', 'Costa Rica', 'Panama', 'Guatemala', 'Honduras', 'El Salvador', 'Nicaragua', 'Belize'],
    codes: ['MX', 'CR', 'PA', 'GT', 'HN', 'SV', 'NI', 'BZ'],
  },
  {
    key: 'south_america',
    countries: ['Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Uruguay', 'Ecuador', 'Venezuela'],
    codes: ['BR', 'AR', 'CL', 'CO', 'PE', 'UY', 'EC', 'VE'],
  },
  {
    key: 'japan',
    countries: ['Japan'],
    codes: ['JP'],
  },
  {
    key: 'rest_of_apac',
    countries: [
      'China',
      'South Korea',
      'Korea',
      'Taiwan',
      'Hong Kong',
      'Singapore',
      'Thailand',
      'Vietnam',
      'Malaysia',
      'Indonesia',
      'Philippines',
      'India',
      'Australia',
      'New Zealand',
      'UAE',
      'United Arab Emirates',
      'Saudi Arabia',
      'Qatar',
      'Kuwait',
      'Israel',
      'Lebanon',
      'Turkey',
    ],
    codes: [
      'CN', 'KR', 'TW', 'HK', 'SG', 'TH', 'VN', 'MY', 'ID', 'PH', 'IN',
      'AU', 'NZ', 'AE', 'SA', 'QA', 'KW', 'IL', 'LB', 'TR',
    ],
  },
];

const COUNTRY_TO_REGION = new Map();
const CODE_TO_REGION = new Map();
for (const region of REGIONS) {
  for (const country of region.countries) {
    COUNTRY_TO_REGION.set(country.toLowerCase(), region.key);
  }
  for (const code of region.codes || []) {
    CODE_TO_REGION.set(code.toUpperCase(), region.key);
  }
}

export function regionForCountry(country) {
  if (!country) return 'rest_of_world';
  return COUNTRY_TO_REGION.get(String(country).trim().toLowerCase()) || 'rest_of_world';
}

/**
 * Map an ISO 3166-1 alpha-2 country code ("DK") to a region key.
 * Unknown / missing codes fall back to `rest_of_world`.
 */
export function regionForCountryCode(code) {
  if (!code) return 'rest_of_world';
  return CODE_TO_REGION.get(String(code).trim().toUpperCase()) || 'rest_of_world';
}

export const REGION_KEYS = REGIONS.map((r) => r.key);
